import React from 'react';
import { motion } from 'framer-motion';
import {
  Syringe, Flame, ArrowRight, ExternalLink, FileText, ShieldAlert, Loader2,
  Download, Stamp, Eye,
} from 'lucide-react';
import { useSection } from '../hooks/usePortalData';

const ICONS = { file: FileText, shield: ShieldAlert, flame: Flame, virus: Syringe };

function ClassifiedDocCard({ dossier, index }) {
  const Icon = ICONS[dossier.icon] || FileText;

  return (
    <motion.article
      data-testid={`covid-doc-${dossier.code}`}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      className="classified-doc-card flex flex-col h-full"
    >
      <div className="classified-doc-card__stripe" />

      <div className="classified-doc-card__header">
        <span className="classified-doc-card__code" data-testid={`covid-doc-code-${dossier.code}`}>
          {dossier.code}
        </span>
        <span className="declassified-chip" data-testid={`covid-doc-badge-${dossier.code}`}>
          <Stamp className="h-3 w-3" /> {dossier.badge || 'DÉCLASSIFIÉ'}
        </span>
      </div>

      <div className="classified-doc-card__body flex flex-col flex-1">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="h-11 w-11 rounded border shrink-0 flex items-center justify-center"
            style={{ borderColor: 'rgba(251,191,36,0.5)', background: 'rgba(251,191,36,0.10)' }}
          >
            <Icon className="h-5 w-5 text-amber-300" />
          </div>
          <div className="min-w-0">
            <h3 className="serif text-lg md:text-xl text-1 leading-tight">{dossier.title}</h3>
            <div className="mono text-[10px] text-3 tracking-wider uppercase mt-1">
              {dossier.date}{dossier.pages ? ` · ${dossier.pages} pages` : ''}
            </div>
          </div>
        </div>

        {dossier.source_authority && (
          <div className="mono text-[10.5px] text-2 leading-relaxed mb-3 pb-3 border-b border-white/5">
            <ShieldAlert className="inline h-3 w-3 mr-1 text-amber-300/80" />
            <span className="text-3">Autorité source : </span>
            <span className="text-2">{dossier.source_authority}</span>
          </div>
        )}

        <p className="text-2 text-[14px] leading-relaxed mb-4 flex-1">
          {dossier.summary}
        </p>

        {dossier.tags && dossier.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {dossier.tags.map((t, i) => (
              <span
                key={i}
                className="mono text-[9px] tracking-widest px-2 py-0.5 rounded border border-amber-400/25 text-amber-200/80 bg-amber-400/5"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-4 border-t border-amber-400/15 flex flex-wrap items-center gap-2">
          {(dossier.ocr_link || dossier.pdf_url) && (
            <a
              href={dossier.ocr_link || dossier.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`covid-doc-view-${dossier.code}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium"
              style={{
                background: 'linear-gradient(180deg, rgba(220,38,38,0.28), rgba(220,38,38,0.14))',
                border: '1px solid rgba(220,38,38,0.6)',
                color: '#fee2e2',
                letterSpacing: '0.06em',
              }}
            >
              <Eye className="h-4 w-4" /> CONSULTER LE DOCUMENT (OCR)
            </a>
          )}
          {dossier.pdf_url && (
            <a
              href={dossier.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`covid-doc-download-${dossier.code}`}
              className="btn-ghost inline-flex items-center gap-2 px-3 py-2.5 rounded-md text-sm"
            >
              <Download className="h-4 w-4" /> Télécharger PDF
            </a>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function DossierCard({ dossier, index }) {
  const Icon = ICONS[dossier.icon] || FileText;
  const hostname = (() => {
    try { return new URL(dossier.link).hostname; } catch { return dossier.link; }
  })();

  return (
    <motion.article
      data-testid={`covid-dossier-${dossier.id}`}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      className="breaking-card flex flex-col h-full"
    >
      <div className="breaking-card__stripe" />
      <div className="breaking-card__body flex flex-col flex-1">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div
            className="h-12 w-12 rounded border shrink-0 flex items-center justify-center"
            style={{ borderColor: 'rgba(220,38,38,0.55)', background: 'rgba(220,38,38,0.12)' }}
          >
            <Icon className="h-5 w-5 text-rose-300" />
          </div>
          {dossier.badge && (
            <span className="breaking-badge">
              <Flame className="h-3 w-3" /> {dossier.badge}
            </span>
          )}
        </div>

        {dossier.tag && (
          <div className="mono text-[10px] tracking-[0.25em] text-rose-300/80 uppercase mb-2">
            {dossier.tag}
          </div>
        )}

        <h3 className="serif text-xl md:text-2xl text-1 leading-tight mb-3">
          {dossier.title}
        </h3>

        <p className="text-2 text-[14.5px] leading-relaxed mb-6 flex-1">
          {dossier.summary}
        </p>

        <div className="pt-4 border-t border-rose-500/20 flex items-center justify-between flex-wrap gap-3">
          <a
            href={dossier.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`covid-dossier-link-${dossier.id}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all"
            style={{
              background: 'linear-gradient(180deg, rgba(220,38,38,0.28), rgba(220,38,38,0.14))',
              border: '1px solid rgba(220,38,38,0.6)',
              color: '#fee2e2',
              letterSpacing: '0.08em',
            }}
          >
            DOSSIER COMPLET <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href={dossier.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mono text-[10px] text-4 hover:text-rose-200 inline-flex items-center gap-1"
          >
            <ExternalLink className="h-3 w-3" /> {hostname}
          </a>
        </div>
      </div>
    </motion.article>
  );
}

export default function CovidPage() {
  const { section, loading, error } = useSection('covid');

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-field">
        <div className="mono text-sm text-3 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-rose-300" /> Chargement des dossiers COVID…
        </div>
      </div>
    );
  }

  if (error || !section) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-field">
        <div className="mono text-sm text-rose-300">
          Impossible de charger les dossiers. {error || 'Section introuvable.'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-field" data-testid="page-covid">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-rose-500/20">
        {section.header_image && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url(${section.header_image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center 30%',
              filter: 'brightness(0.4) contrast(1.05) saturate(0.9)',
            }}
          />
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(30,10,10,0.35) 0%, rgba(35,15,15,0.65) 55%, rgba(45,45,45,0.95) 100%)',
          }}
        />
        <div className="absolute inset-0 bg-grid opacity-15 pointer-events-none" />

        <div className="mx-auto max-w-[1400px] px-6 py-16 relative">
          <div className="inline-flex items-center gap-2 mono text-[10px] tracking-[0.28em] text-rose-300/95 uppercase mb-6 px-3 py-1 rounded-full border border-rose-400/40 bg-rose-500/10 backdrop-blur">
            <Syringe className="h-3 w-3" /> Archives Souveraines · {section.label} · {section.title}
          </div>

          <h1 className="serif font-medium leading-[0.95] tracking-tight text-1 mb-4 drop-shadow-2xl">
            <span className="block text-4xl md:text-5xl lg:text-6xl">
              <span className="text-1">Dossiers</span>{' '}
              <span className="italic text-rose-300">COVID</span>
            </span>
            <span className="block mt-3 text-xl md:text-2xl lg:text-3xl text-2 font-light italic">
              {section.subtitle}
            </span>
          </h1>

          <p className="text-2 max-w-3xl text-base leading-relaxed mt-4">
            {section.description}
          </p>

          <div className="mt-6 flex flex-wrap gap-3 mono text-[11px] text-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-rose-400/40 bg-rose-500/10 text-rose-200">
              <Flame className="h-3 w-3" /> {section.entries.length} DOSSIERS · PREUVES DÉFINITIVES
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-white/10">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" /> Accès public · Sans mot de passe
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-white/10">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Source : ah2020.fr
            </span>
          </div>
        </div>

        <div className="breaking-card__stripe" />
      </section>

      {/* Section title */}
      <section className="mx-auto max-w-[1400px] px-6 pt-12 pb-4">
        <div className="flex items-center gap-4">
          <Syringe className="h-6 w-6 text-rose-300" />
          <h2 className="serif text-2xl md:text-3xl text-1 tracking-wide uppercase">
            Dossiers COVID <span className="text-rose-300">— La Vérité Confirmée</span>
          </h2>
        </div>
        <div className="mt-2 mono text-[10px] tracking-[0.3em] text-3 uppercase">
          {section.entries.length} dossiers · Preuves irréfutables · Une même vérité
        </div>
      </section>

      {/* Dossiers grid */}
      <section className="mx-auto max-w-[1400px] px-6 pt-4 pb-12">
        {(() => {
          const classified = section.entries.filter(e => e.pdf_url && e.code);
          const editorial = section.entries.filter(e => !(e.pdf_url && e.code));
          return (
            <>
              {classified.length > 0 && (
                <div className="mb-10" data-testid="covid-classified-block">
                  <div className="mb-4 flex items-center gap-3">
                    <Stamp className="h-4 w-4 text-amber-300" />
                    <div className="mono text-[10px] tracking-[0.28em] text-amber-300 uppercase">
                      Documents officiels déclassifiés · Consultation OCR
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="covid-classified-grid">
                    {classified.map((d, i) => (
                      <ClassifiedDocCard key={d.id} dossier={d} index={i} />
                    ))}
                  </div>
                </div>
              )}

              {editorial.length > 0 && (
                <div data-testid="covid-editorial-block">
                  <div className="mb-4 flex items-center gap-3">
                    <Flame className="h-4 w-4 text-rose-400" />
                    <div className="mono text-[10px] tracking-[0.28em] text-rose-300 uppercase">
                      Dossiers d'analyse · Ah2020.fr
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="covid-dossiers-grid">
                    {editorial.map((d, i) => (
                      <DossierCard key={d.id} dossier={d} index={i} />
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </section>

      {/* Conclusion */}
      <section className="mx-auto max-w-[1400px] px-6 pb-16">
        <div className="breaking-card relative">
          <div className="breaking-card__stripe" />
          <div className="p-6 md:p-8">
            <div className="mono text-[10px] tracking-[0.28em] text-rose-300 uppercase mb-3 inline-flex items-center gap-2">
              <Flame className="h-3 w-3" /> Conclusion — La Justice Arrive
            </div>
            <p className="serif italic text-[15px] md:text-[17px] leading-relaxed text-rose-100/90 max-w-4xl">
              Le rapport de la Chambre, l'invocation du 5ème Amendement et les 1 141 pages de journaux
              intimes convergent vers une même vérité : le SARS-CoV-2 est un produit de laboratoire,
              son évasion a été dissimulée, et les responsables étaient conscients dès janvier 2020.
              Le silence de Fauci est son aveu le plus éloquent.{' '}
              <strong className="text-amber-300">La justice arrive.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Editorial note */}
      <section className="mx-auto max-w-[1400px] px-6 pb-16">
        <div className="glass rounded-lg p-5 border border-rose-400/20">
          <div className="mono text-[10px] tracking-[0.25em] text-rose-300/80 uppercase mb-2">
            Note éditoriale
          </div>
          <p className="text-2 text-sm leading-relaxed">
            Les dossiers présentés compilent les publications officielles du Congrès américain et les
            documents rendus publics par la commission sénatoriale. Pour l'analyse détaillée et les
            sources primaires, se référer aux dossiers complets sur <em>ah2020.fr</em>.
          </p>
        </div>
      </section>
    </div>
  );
}
